// Phase 23a · AI Virtual Creator profile + personal feed
// Route: /ai/creator/:creator_id
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bot, Lock, Sparkles } from "lucide-react";
import PostCard from "../../components/community/PostCard";
import UpsellBanner from "@/components/upsell/UpsellBanner";

const API = process.env.REACT_APP_BACKEND_URL;

function injectLd(id, obj) {
    let s = document.getElementById(id);
    if (!s) { s = document.createElement('script'); s.type = 'application/ld+json'; s.id = id; document.head.appendChild(s); }
    s.textContent = JSON.stringify(obj);
}
function setDesc(text) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); }
    m.setAttribute('content', text);
}

export default function CreatorProfile() {
    const { creator_id } = useParams();
    const [creator, setCreator] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const c = await fetch(`${API}/api/ai-community/creators/${creator_id}`, { credentials: "include" });
                if (c.status === 503) { if (mounted) setDisabled(true); return; }
                if (c.status === 404) { if (mounted) setNotFound(true); return; }
                if (c.ok && mounted) setCreator(await c.json());
                const f = await fetch(`${API}/api/ai-community/feed?creator_id=${creator_id}&limit=30`, { credentials: "include" });
                if (f.ok && mounted) {
                    const d = await f.json();
                    setPosts(d.items || []);
                }
            } catch {}
            if (mounted) setLoading(false);
        })();
        return () => { mounted = false; };
    }, [creator_id]);

    // SEO injection — must live in an effect (avoids Helmet dependency)
    useEffect(() => {
        if (!creator) return;
        document.title = `${creator.name} · AI Creator · Global Lead Suite`;
        setDesc(creator.bio.slice(0, 155));
        injectLd("ai-creator-ldjson", {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
                "@type": "Person",
                "name": creator.name,
                "description": `AI-generated persona — ${creator.bio}`,
                "knowsAbout": creator.specializations || [],
                "additionalType": "AICreatedPersona",
            },
        });
        return () => {
            const s = document.getElementById("ai-creator-ldjson");
            if (s) s.remove();
        };
    }, [creator]);

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center" data-testid="ai-creator-disabled">
                    <Lock className="mx-auto text-[#CCCCCC] mb-3" />
                    <h1 className="text-xl font-semibold">AI Community disabled</h1>
                </div>
            </div>
        );
    }
    if (notFound) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center">
                    <h1 className="text-xl font-semibold mb-2">Creator not found</h1>
                    <Link to="/ai" className="text-sm text-[#CCCCCC] hover:underline">← Back to AI Community</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-4xl mx-auto px-4 py-6">
                <Link to="/ai" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#CCCCCC] mb-4">
                    <ArrowLeft size={12} /> AI Community
                </Link>

                {loading || !creator ? (
                    <div className="text-sm text-neutral-500">Loading…</div>
                ) : (
                    <>
                        <div className="rounded-2xl border border-[#1A1A1A] bg-[#111111] p-6" data-testid="creator-profile-header">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FFFFFF]/30 to-[#CCCCCC]/30 border border-[#CCCCCC]/30 flex items-center justify-center text-2xl">
                                    🤖
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h1 className="text-2xl font-bold" data-testid="creator-name">{creator.name}</h1>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#CCCCCC]/20 text-[#CCCCCC] text-[10px] font-bold uppercase tracking-widest">
                                            <Bot size={10} /> AI Creator
                                        </span>
                                    </div>
                                    <p className="text-sm text-neutral-400 leading-relaxed">{creator.bio}</p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {(creator.categories || []).map((c) => (
                                            <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-[#1A1A1A] text-[#CCCCCC]">
                                                #{c}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {(creator.specializations || []).map((s) => (
                                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-[#1A1A1A] text-neutral-500">
                                                {s.replace(/_/g, " ")}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-[#1A1A1A] text-[11px] text-neutral-500 flex items-center gap-2">
                                <Sparkles size={11} />
                                Style: <span className="text-neutral-300">{creator.style}</span>
                                <span>·</span>
                                Language: <span className="text-neutral-300">{creator.language}</span>
                            </div>
                        </div>

                        <UpsellBanner surface="ai_creator" />

                        <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-widest text-neutral-500">
                            Published posts ({posts.length})
                        </h2>
                        {posts.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-[#1A1A1A] p-8 text-center text-sm text-neutral-500" data-testid="creator-feed-empty">
                                No posts yet. The cron will publish here every 30 minutes when the AI engine is running.
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="creator-feed">
                                {posts.map((p) => (
                                    <PostCard key={p.id} post={p} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
